import { AuditEndpoint, AuditRecentVisit, AuditUserActivity } from '../models';
import * as St from '../styles';
import { formatNumber } from '../utils';
import CardStatus, { CardStatusProps } from './CardStatus';

interface TableCardProps extends CardStatusProps {
  title: string;
  subtitle: string;
  badge: string;
  maxHeight: number;
  minWidth?: number;
  children: React.ReactNode;
}

const TableCard: React.FC<TableCardProps> = ({
  title,
  subtitle,
  badge,
  maxHeight,
  minWidth,
  loading,
  error,
  children,
}) => (
  <St.TableCard>
    <St.CardHeader>
      <St.CardTitle>
        <h3>{title}</h3>
        <span>{subtitle}</span>
      </St.CardTitle>
      <St.Badge>{badge}</St.Badge>
    </St.CardHeader>
    <CardStatus loading={loading} error={error} height={maxHeight}>
      <St.ScrollList maxHeight={maxHeight}>
        <St.Table minWidth={minWidth}>{children}</St.Table>
      </St.ScrollList>
    </CardStatus>
  </St.TableCard>
);

export const EndpointsTable: React.FC<
  { endpoints?: AuditEndpoint[] } & CardStatusProps
> = ({ endpoints = [], loading, error }) => (
  <TableCard
    loading={loading}
    error={error}
    title="Endpoints de API"
    subtitle="Requisições vinculadas às visitas selecionadas"
    badge="TOP 30"
    maxHeight={330}
  >
    <thead>
      <tr>
        <th>ENDPOINT / SERVIÇO</th>
        <th>MÉTODO</th>
        <th className="center">CHAMADAS DE API</th>
        <th className="center">IDS</th>
      </tr>
    </thead>
    <tbody>
      {endpoints.length === 0 && (
        <tr>
          <td colSpan={4}>Nenhuma chamada no período.</td>
        </tr>
      )}
      {endpoints.map((item) => (
        <tr key={`${item.metodo} ${item.endpoint}`}>
          <td>
            {item.endpoint}
            <small>{item.servico}</small>
          </td>
          <td>{item.metodo}</td>
          <td className="center">{formatNumber(item.chamadas)}</td>
          <td className="center">{formatNumber(item.usuarios)}</td>
        </tr>
      ))}
    </tbody>
  </TableCard>
);

export const UserActivityTable: React.FC<
  { users?: AuditUserActivity[] } & CardStatusProps
> = ({ users = [], loading, error }) => (
  <TableCard
    loading={loading}
    error={error}
    title="Atividade por usuário"
    subtitle="Selecione um registro para filtrar"
    badge="TOP 50"
    maxHeight={330}
  >
    <thead>
      <tr>
        <th>USUÁRIO REGISTRADO</th>
        <th className="center">VISITAS</th>
        <th className="center">CHAMADAS DE API</th>
        <th className="center">SERVIÇOS</th>
      </tr>
    </thead>
    <tbody>
      {users.length === 0 && (
        <tr>
          <td colSpan={4}>Nenhum usuário no período.</td>
        </tr>
      )}
      {users.map((item) => (
        <tr key={item.idUsuario}>
          <td>
            {item.usuario}
            <small>{item.idUsuario}</small>
          </td>
          <td className="center">{formatNumber(item.visitas)}</td>
          <td className="center">{formatNumber(item.chamadas)}</td>
          <td className="center">{item.servicos}</td>
        </tr>
      ))}
    </tbody>
  </TableCard>
);

// Até 100 visitas, as mais recentes primeiro (o contrato não pagina).
export const RecentVisitsTable: React.FC<
  { visits?: AuditRecentVisit[] } & CardStatusProps
> = ({ visits = [], loading, error }) => (
  <TableCard
    loading={loading}
    error={error}
    title="Visitas recentes"
    subtitle="Selecione uma visita para ver as chamadas de API associadas"
    badge="ATÉ 100"
    maxHeight={330}
    minWidth={1000}
  >
    <thead>
      <tr>
        <th>DATA E HORA</th>
        <th>USUÁRIO / ID</th>
        <th>TELA / ROTA DO FRONT-END</th>
        <th>PRIMEIRA CHAMADA DE API</th>
      </tr>
    </thead>
    <tbody>
      {visits.length === 0 && (
        <tr>
          <td colSpan={4}>Nenhuma visita no período.</td>
        </tr>
      )}
      {visits.map((item) => (
        <tr key={item.correlationId}>
          <td>{item.dataHora}</td>
          <td>
            {item.usuario}
            <small>{item.idUsuario}</small>
          </td>
          <td>
            {item.tela}
            <small>{item.rota}</small>
          </td>
          <td className="url">
            {item.apiPath}
            <small>
              {formatNumber(item.chamadas)}{' '}
              {item.chamadas === 1 ? 'chamada' : 'chamadas'} na visita
            </small>
          </td>
        </tr>
      ))}
    </tbody>
  </TableCard>
);
